/* Dashboard — the reference's landing page, with Qorvex's data in it.
 *
 * Three columns of cards and a full-width card beneath, laid out exactly as
 * the AlgoBulls dashboard is:
 *
 *   ACTIVE SUBSCRIPTIONS        PROFILE INFORMATION      YOUR PORTFOLIO ANALYSIS
 *   AVAILABLE MARGIN            ACCOUNT INFORMATION      (strategies table)
 *   YOUR RECENT ORDERS FROM P&L AND TRADE BOOK
 *
 * Every figure is real or explicitly absent. Where the reference shows a plan
 * and a credit balance, this shows the trader's Atlas subscriptions and the
 * account's available margin, because those are the things that exist here.
 * Nothing is a placeholder dressed up as data.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { useStore } from '@/store'
import { Icon } from '@/components/Icons'
import { Card, KvRow, ModeDot, ProductPill, RangeChips, Tabs, useRange } from '@/panels/dashboardBits'
import { fmtDateTime, fmtMoney, fmtPrice, fmtQty, fmtSigned, fmtTime } from '@/lib/format'
import { PRODUCTS } from '@/lib/products'
import type { Page } from '@/components/Terminal'
import type { AccountBalances, AtlasSubscription, Strategy } from '@/types'

const REFRESH_MS = 30_000

export function Dashboard({ onNavigate, user }: {
  onNavigate: (p: Page) => void
  user?: { email: string }
}) {
  const roles = useStore((s) => s.roles)
  const mode = useStore((s) => s.tradingMode)
  const killSwitch = useStore((s) => s.killSwitchEngaged)
  const positions = useStore((s) => s.positions)
  const orders = useStore((s) => s.orders)
  const executions = useStore((s) => s.executions)
  const can = useStore((s) => s.can)

  const [subs, setSubs] = useState<AtlasSubscription[] | null>(null)
  const [strategies, setStrategies] = useState<Strategy[] | null>(null)
  const [account, setAccount] = useState<{
    name: string; code: string; mode: string; currency: string
    available: string; used: string; cash: string
  } | null>(null)
  const [balances, setBalances] = useState<AccountBalances | null>(null)
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null)

  const load = useCallback(async () => {
    const [s, st, a] = await Promise.allSettled([
      api.atlasSubscriptions(true),
      api.strategies(),
      api.accounts(),
    ])
    if (s.status === 'fulfilled') setSubs(s.value); else setSubs([])
    if (st.status === 'fulfilled') setStrategies(st.value.items ?? []); else setStrategies([])
    if (a.status === 'fulfilled') {
      const first = a.value.items.find((x) => x.can_trade) ?? a.value.items[0]
      if (first) {
        setAccount({
          name: first.name, code: first.code, mode: first.trading_mode,
          currency: first.base_currency, available: first.margin_available,
          used: first.margin_used, cash: first.cash_balance,
        })
        try { setBalances(await api.balances(first.id)) } catch { setBalances(null) }
      }
    }
    setRefreshedAt(Date.now())
  }, [])

  useEffect(() => {
    let cancelled = false
    const tick = () => { if (!cancelled) void load() }
    tick()
    const t = setInterval(tick, REFRESH_MS)
    return () => { cancelled = true; clearInterval(t) }
  }, [load])

  const activeSubs = useMemo(() => (subs ?? []).filter((s) => s.status === 'ACTIVE'), [subs])
  const cancelledSubs = useMemo(() => (subs ?? []).filter((s) => s.status !== 'ACTIVE'), [subs])
  const running = useMemo(
    () => (subs ?? []).filter((s) => s.status === 'ACTIVE' && s.run_status === 'RUNNING').length,
    [subs])
  const openPositions = positions.filter((p) => Number(p.quantity) !== 0)
  const working = orders.filter((o) =>
    ['SUBMITTED', 'ACKNOWLEDGED', 'PARTIALLY_FILLED', 'PENDING_AMEND', 'PENDING_CANCEL']
      .includes(o.status)).length

  const currency = account?.currency ?? balances?.currency ?? 'INR'
  const available = account?.available ?? balances?.available_margin ?? null
  const used = account?.used ?? balances?.used_margin ?? null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div className="qx-dash-grid">
        {/* ---------- column 1 ---------- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <Card title="Active subscriptions"
                info={`${PRODUCTS.marketplace.name} subscriptions on this account. A subscription runs nothing by itself; deploying is a separate, risk-checked step.`}>
            <div className="qx-inset">
              <KvRow icon="layers" label="Active"
                     value={activeSubs.length === 0
                       ? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>None</span>
                       : activeSubs.slice(0, 3).map((s) => (
                           <span key={s.id} style={{ display: 'block' }}>
                             {s.title}
                             <span className="sub">
                               {s.tier === 'OPEN' ? 'Atlas Open' : 'Atlas Edge'} · since {fmtDateTime(s.subscribed_at)}
                               {s.run_status ? ` · ${s.run_status}` : ''}
                             </span>
                           </span>
                         ))} />
              <KvRow icon="history" label="Cancelled"
                     value={cancelledSubs.length === 0
                       ? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>None</span>
                       : cancelledSubs.slice(0, 2).map((s) => (
                           <span key={s.id} style={{ display: 'block' }}>
                             {s.title}
                             <span className="sub warn">
                               cancelled {s.cancelled_at ? fmtDateTime(s.cancelled_at) : ''}
                             </span>
                           </span>
                         ))} />
              <div className="qx-kv" style={{ borderTop: '1px solid var(--border-strong)' }}>
                <span className="qx-kv-label" style={{ fontSize: 'var(--fs-sm)' }}>
                  Suggestion based on usage
                </span>
                <button className="qx-link qx-link-sm" onClick={() => onNavigate('atlas')}>
                  Browse {PRODUCTS.marketplace.name} <Icon name="external" size={12} style={{ display: 'inline', verticalAlign: '-2px' }} />
                </button>
              </div>
            </div>
          </Card>

          <Card>
            <div className="qx-inset" style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
                <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  Available margin
                  <button className="qx-link" onClick={() => onNavigate('broking')} title="Broking details"
                          style={{ display: 'inline-flex' }}>
                    <Icon name="external" size={13} />
                  </button>
                </span>
                <span className="num" style={{ fontSize: 22, fontWeight: 700 }}>
                  {available == null ? '—' : fmtMoney(available, currency)}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', marginTop: 'var(--sp-3)' }}>
              <span style={{ flex: 1, fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                {account
                  ? <>{account.name} <span style={{ color: 'var(--text-muted)' }}>({account.code} · {account.mode})</span>
                      {used != null && <><br />Used margin {fmtMoney(used, currency)} · Cash {fmtMoney(account?.cash ?? balances?.cash, currency)}</>}</>
                  : 'No trading account is granted to this user.'}
              </span>
              <button className="qx-btn qx-btn-primary" onClick={() => onNavigate('portfolio')}>
                My Portfolio
              </button>
            </div>
          </Card>
        </div>

        {/* ---------- column 2 ---------- */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <Card title="Profile information"
                info="Who you are signed in as, what you may do, and the mode this session trades in."
                footer={
                  <button className="qx-btn qx-btn-outline" onClick={() => onNavigate('settings')}>
                    Change Profile Settings
                  </button>
                }>
            <div className="qx-inset">
              <KvRow icon="user" label="Signed in as" value={user?.email ?? '—'} />
              <KvRow icon="key" label="Roles"
                     value={roles.length ? roles.join(', ') : 'none'} />
              <KvRow icon="target" label="Trading mode"
                     tone={mode === 'LIVE' ? 'danger' : mode === 'PAPER' ? 'warn' : 'neutral'}
                     value={mode}
                     sub={mode === 'LIVE' ? 'Real orders are sent to a venue' : 'Simulated — no real orders are sent'} />
              <KvRow icon="check" label="Interested products"
                     value={<>
                       <span style={{ display: 'block' }}>{PRODUCTS.marketplace.name} <Ok /></span>
                       <span style={{ display: 'block' }}>{PRODUCTS.studio.name} <Ok /></span>
                     </>} />
            </div>
          </Card>

          <Card title="Account information"
                info="Live counts from this account. A dash means the figure is unavailable, not zero.">
            <div className="qx-inset">
              <KvRow icon="file" label="Strategies saved" info="Strategies you own in Forge, any lane."
                     value={strategies ? String(strategies.length) : '—'} />
              <KvRow icon="play" label="Running strategies" info="Atlas subscriptions with a run in RUNNING state."
                     value={subs ? String(running) : '—'} />
              <KvRow icon="layers" label="Open positions" value={String(openPositions.length)} />
              <KvRow icon="clock" label="Working orders" value={String(working)} />
              <KvRow icon="power" label="Kill switch"
                     tone={killSwitch ? 'danger' : 'ok'}
                     value={killSwitch ? 'ENGAGED' : 'Clear'} />
              <KvRow icon="shield" label="Order permission"
                     tone={can('order:create') ? 'ok' : 'warn'}
                     value={can('order:create') ? 'ON' : 'OFF'} />
            </div>
          </Card>
        </div>

        {/* ---------- column 3 ---------- */}
        <PortfolioAnalysis
          strategies={strategies} subs={subs}
          onNavigate={onNavigate} onRefresh={() => void load()} refreshedAt={refreshedAt}
        />
      </div>

      <RecentBook positions={openPositions} executions={executions} onNavigate={onNavigate} />
    </div>
  )
}

function Ok() {
  return <span aria-label="enabled" title="Enabled" style={{ color: 'var(--ok-solid)' }}>●</span>
}

/* ------------------------------------------------- your portfolio analysis */
type AnalysisTab = 'strategies' | 'subscriptions'

function PortfolioAnalysis({ strategies, subs, onNavigate, onRefresh, refreshedAt }: {
  strategies: Strategy[] | null
  subs: AtlasSubscription[] | null
  onNavigate: (p: Page) => void
  onRefresh: () => void
  refreshedAt: number | null
}) {
  const [tab, setTab] = useState<AnalysisTab>('strategies')
  const range = useRange('7D')

  const rows = useMemo(() => {
    if (tab === 'strategies') {
      return (strategies ?? [])
        .filter((s) => range.includes(s.created_at))
        .slice(0, 6)
    }
    return (subs ?? [])
      .filter((s) => range.includes(s.subscribed_at))
      .slice(0, 6)
  }, [tab, strategies, subs, range])

  return (
    <Card
      title="Your portfolio analysis"
      info="The strategies you own and the Atlas strategies you subscribe to. Opening one takes you to the screen where it is deployed, with its approval and risk checks."
      noPadding
      footer={
        <button className="qx-btn qx-btn-outline" onClick={() => onNavigate('portfolio')}>
          View All in “My Portfolio”
        </button>
      }
      style={{ gridRow: 'span 2' }}
    >
      <div style={{ padding: 'var(--sp-3) var(--sp-4) 0', display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'center' }}>
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { id: 'strategies', label: 'My strategies', icon: 'blocks', count: strategies?.length },
            { id: 'subscriptions', label: PRODUCTS.marketplace.name, icon: 'store', count: subs?.length },
          ]}
        />
        <span style={{ marginLeft: 'auto' }}><RangeChips range={range} /></span>
      </div>
      <div style={{ padding: '6px var(--sp-3) 0', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <button className="qx-btn-icon" title={refreshedAt ? `Refreshed ${fmtTime(refreshedAt)}` : 'Refresh'} onClick={onRefresh}>
          <Icon name="refresh" size={16} />
        </button>
        <button className="qx-btn-icon" title="Settings" onClick={() => onNavigate('settings')}>
          <Icon name="settings" size={16} />
        </button>
        <button className="qx-btn-icon" title="Open My Portfolio" onClick={() => onNavigate('portfolio')}>
          <Icon name="expand" size={16} />
        </button>
      </div>

      <div className="scroll-x" style={{ marginTop: 6 }}>
        <table className="qx">
          <thead>
            <tr>
              <th>Mode</th>
              <th>Strategy</th>
              <th>Instrument(s)</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="qx-empty">
                {tab === 'strategies'
                  ? (strategies === null ? 'Loading…' : `No strategies in this range. Build one in ${PRODUCTS.studio.name}.`)
                  : (subs === null ? 'Loading…' : `No ${PRODUCTS.marketplace.name} subscriptions in this range.`)}
              </td></tr>
            )}
            {tab === 'strategies' && (rows as Strategy[]).map((s) => (
              <tr key={s.id}>
                <td><ModeDot mode={s.approved_for_live ? 'LIVE' : 'PAPER'}
                             title={s.approved_for_live ? 'Approved for live' : 'Paper and backtest only'} /></td>
                <td>
                  <button className="qx-link" onClick={() => onNavigate('forge')} style={{ whiteSpace: 'normal', textAlign: 'left' }}>
                    {s.name}
                  </button>
                  <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>{s.code}</div>
                </td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <ProductPill label={s.language === 'PYTHON' ? 'PY' : 'FORGE'} />
                    <span>
                      <b>{s.language === 'PYTHON' ? 'Python' : 'Rule graph'}</b>
                      <span style={{ display: 'block', fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                        v{s.current_version ?? '—'}
                      </span>
                    </span>
                  </span>
                </td>
                <td><span className={`qx-status ${s.status}`}>{s.status}</span></td>
                <td><button className="qx-btn qx-btn-primary" onClick={() => onNavigate('forge')}>Open</button></td>
              </tr>
            ))}
            {tab === 'subscriptions' && (rows as AtlasSubscription[]).map((s) => (
              <tr key={s.id}>
                <td><ModeDot mode={(s.last_deployed_mode as 'PAPER' | 'LIVE' | 'BACKTEST' | null) ?? 'BACKTEST'}
                             title={s.last_deployed_mode ? `Last deployed ${s.last_deployed_mode}` : 'Never deployed'} /></td>
                <td>
                  <button className="qx-link" onClick={() => onNavigate('atlas')} style={{ whiteSpace: 'normal', textAlign: 'left' }}>
                    {s.title}
                  </button>
                  <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>{s.slug}</div>
                </td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <ProductPill label={s.tier} />
                    <span>
                      <b>{s.risk_grade}</b>
                      <span style={{ display: 'block', fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                        v{s.strategy_version}
                      </span>
                    </span>
                  </span>
                </td>
                <td><span className={`qx-status ${s.run_status ?? s.status}`}>{s.run_status ?? s.status}</span></td>
                <td><button className="qx-btn qx-btn-primary" onClick={() => onNavigate('atlas')}>Open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

/* --------------------------------------------- recent orders: P&L and trades */
type BookTab = 'pnl' | 'trades'

function RecentBook({ positions, executions, onNavigate }: {
  positions: ReturnType<typeof useStore.getState>['positions']
  executions: ReturnType<typeof useStore.getState>['executions']
  onNavigate: (p: Page) => void
}) {
  const [tab, setTab] = useState<BookTab>('pnl')
  const [pct, setPct] = useState(false)
  const range = useRange('1D')
  const setBlotter = useStore((s) => s.setActiveBlotter)

  const trades = useMemo(
    () => executions.filter((e) => range.includes(e.exec_time)).slice(0, 25),
    [executions, range])

  const download = () => {
    const rows = tab === 'pnl'
      ? [['Symbol', 'Mode', 'Qty', 'Avg', 'Last', 'Realised', 'Unrealised', 'Total'],
         ...positions.map((p) => [p.symbol ?? p.instrument_id, p.trading_mode, p.quantity, p.avg_price,
                                  p.last_price ?? '', p.realized_pnl, p.unrealized_pnl, p.total_pnl])]
      : [['Time', 'Symbol', 'Mode', 'Side', 'Qty', 'Price', 'Gross', 'Costs', 'Net'],
         ...trades.map((e) => [e.exec_time, e.symbol ?? e.instrument_id, e.trading_mode, e.side,
                               e.quantity, e.price, e.gross_value, e.total_costs, e.net_value])]
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const a = document.createElement('a')
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    a.download = `${tab === 'pnl' ? 'pnl' : 'trades'}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  return (
    <Card title="Your recent orders from P&L and trade book"
          info="Open positions with their P&L, and the executions in the range you choose. The full blotters are on the Trade page."
          noPadding>
      <div style={{ padding: 'var(--sp-3) var(--sp-4) 0', display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-3)', alignItems: 'center' }}>
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { id: 'pnl', label: 'P&L Table', icon: 'trend', count: positions.length },
            { id: 'trades', label: 'Trade Table', icon: 'bolt', count: trades.length },
          ]}
        />
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <RangeChips range={range} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>
            <button className={`qx-switch${pct ? ' on' : ''}`} role="switch" aria-checked={pct}
                    title="Show P&L as a percentage of exposure" onClick={() => setPct((v) => !v)}
                    style={{ width: 36, height: 18 }}>
              <span className="knob" style={{ width: 14, height: 14, left: pct ? 20 : 2 }} />
            </button>
            %
          </span>
          <button className="qx-btn-icon" title="Download as CSV" onClick={download}><Icon name="download" size={16} /></button>
          <button className="qx-btn-icon" title="Open the blotters"
                  onClick={() => { setBlotter(tab === 'pnl' ? 'positions' : 'executions'); onNavigate('trade') }}>
            <Icon name="expand" size={16} />
          </button>
        </span>
      </div>

      <div className="scroll-x" style={{ marginTop: 'var(--sp-2)' }}>
        {tab === 'pnl' ? (
          <table className="qx">
            <thead>
              <tr>
                <th>Instrument</th><th>Mode</th>
                <th className="r">Qty</th><th className="r">Avg</th><th className="r">Last</th>
                <th className="r">Realised</th><th className="r">Unrealised</th><th className="r">Total P&L</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 && (
                <tr><td colSpan={8} className="qx-empty">No open positions.</td></tr>
              )}
              {positions.map((p) => {
                const exposure = Math.abs(Number(p.exposure) || 0)
                const show = (v: string) => pct && exposure > 0
                  ? `${((Number(v) / exposure) * 100).toFixed(2)}%`
                  : fmtSigned(v)
                return (
                  <tr key={p.id}>
                    <td><b>{p.symbol ?? p.instrument_id.slice(0, 8)}</b>
                      <span style={{ display: 'block', fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                        {p.exchange ?? ''} {p.product}
                      </span>
                    </td>
                    <td><ModeDot mode={p.trading_mode} /></td>
                    <td className="num r">{fmtQty(p.quantity)}</td>
                    <td className="num r">{fmtPrice(p.avg_price)}</td>
                    <td className="num r">{fmtPrice(p.last_price)}</td>
                    <td className="num r" style={{ color: Number(p.realized_pnl) >= 0 ? 'var(--profit)' : 'var(--loss)' }}>{show(p.realized_pnl)}</td>
                    <td className="num r" style={{ color: Number(p.unrealized_pnl) >= 0 ? 'var(--profit)' : 'var(--loss)' }}>{show(p.unrealized_pnl)}</td>
                    <td className="num r" style={{ fontWeight: 700, color: Number(p.total_pnl) >= 0 ? 'var(--profit)' : 'var(--loss)' }}>{show(p.total_pnl)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <table className="qx">
            <thead>
              <tr>
                <th>Time</th><th>Instrument</th><th>Mode</th><th>Side</th>
                <th className="r">Qty</th><th className="r">Price</th>
                <th className="r">Gross</th><th className="r">Costs</th><th className="r">Net</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 && (
                <tr><td colSpan={9} className="qx-empty">No executions in this range.</td></tr>
              )}
              {trades.map((e) => (
                <tr key={e.id}>
                  <td className="num">{fmtDateTime(e.exec_time)}</td>
                  <td><b>{e.symbol ?? e.instrument_id.slice(0, 8)}</b></td>
                  <td><ModeDot mode={e.trading_mode} /></td>
                  <td style={{ fontWeight: 700, color: e.side === 'BUY' ? 'var(--buy)' : 'var(--sell)' }}>{e.side}</td>
                  <td className="num r">{fmtQty(e.quantity)}</td>
                  <td className="num r">{fmtPrice(e.price)}</td>
                  <td className="num r">{fmtMoney(e.gross_value, e.currency)}</td>
                  <td className="num r">{fmtMoney(e.total_costs, e.currency)}</td>
                  <td className="num r" style={{ fontWeight: 700 }}>{fmtMoney(e.net_value, e.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  )
}
